import React, { useEffect, useState } from 'react';
import { PreRegistrationApi, PreRegistrationShop, PreRegistration } from './lib/pre-registration-api';
import { StyleOverwrites } from './providers/StyleOverwrites';
import { ThemeStyleOverwrites } from './providers/ThemeStyleOverwrites';
import { Header } from './components/header/Header';
import { CustomerDetailsForm } from './components/customer-details-form/CustomerDetailsForm';
import { revealValidationSummary } from './components/customer-details-form/validationSummaryReveal';
import { MainButton } from './components/shared/MainButton';
import { PreRegistrationEventProvider } from './providers/PreRegistrationEventProvider';
import { useLanguage } from './providers/LanguageProvider';
import { FormComponent } from './lib/TicketApi';
import * as m from '@/paraglide/messages.js';

/**
 * The closing moment in the visitor's language — the old date-fns pattern spelled
 * the month and the "at" in English whatever language the widget was showing.
 */
const formatClosingDate = (availableUntil: string, locale: string): string =>
    new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(availableUntil));

interface PreRegistrationContainerProps {
    shopSlug: string;
    language?: string;
}

const PageBackground: React.FC<{ shop: PreRegistrationShop; children: React.ReactNode }> = ({ shop, children }) => {
    const theme = shop.theme?.light;
    const backgroundImage = theme ? theme.backgroundImage?.url : shop.style.pageBackgroundImage;
    const backgroundFromColor = theme ? theme.background : (shop.style.pageBackgroundFromColor || '#ffffff');
    const backgroundToColor = theme ? theme.background : (shop.style.pageBackgroundToColor || '#ffffff');
    const style = {
        background: backgroundImage
            ? `url(${backgroundImage}) center/cover fixed no-repeat, linear-gradient(to bottom, ${backgroundFromColor}, ${backgroundToColor})`
            : `linear-gradient(to bottom, ${backgroundFromColor}, ${backgroundToColor})`
    };

    return (
        <div className="min-h-screen sm:p-10" style={style}>
            <div className="max-w-[800px] mx-auto rounded-3xl pb-6 shadow-2xl mb-[20px]" style={{
                backgroundColor: theme ? theme.card : (shop.style.panelBackgroundColor || '#ffffff'),
                color: theme ? theme.cardForeground : (shop.style.textColor || '#000000'),
            }}>
                {children}
            </div>
        </div>
    );
};

export function PreRegistrationContainer({ shopSlug, language }: PreRegistrationContainerProps) {
    const { currentLanguage } = useLanguage();
    const activeLanguage = language ?? currentLanguage;
    const [api] = useState(() => new PreRegistrationApi(import.meta.env.VITE_API_URL, shopSlug));
    const [shop, setShop] = useState<PreRegistrationShop | null>(null);
    const [registration, setRegistration] = useState<PreRegistration | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [answers, setAnswers] = useState<Record<string, string | string[] | object[]>>({});
    const [isSuccess, setIsSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [successMessageHtml, setSuccessMessageHtml] = useState<string | null>(null);

    const isPreRegistrationAvailable = (shop: PreRegistrationShop): boolean => {
        if (!shop.availableUntil) return true;
        const endDate = new Date(shop.availableUntil);
        return endDate > new Date();
    };

    useEffect(() => {
        const loadShop = async () => {
            try {
                const shopData = await api.loadShop(activeLanguage);
                setShop(shopData);
                const registrationData = await api.createRegistration(activeLanguage);
                setRegistration(registrationData);
            } catch (e) {
                setError(m.pre_registration_load_failed());
                console.error(e);
            } finally {
                setIsLoading(false);
            }
        };

        loadShop();
    }, [api, activeLanguage]);

    const handleSubmit = async () => {
        if (!registration) return;
        setError(null);
        setIsSubmitting(true);

        try {
            const updatedRegistration = await api.submitRegistration(answers, activeLanguage);
            setRegistration(updatedRegistration);

            if (Object.keys(updatedRegistration.errors).length === 0) {
                setSuccessMessage(updatedRegistration.message ?? null);
                setSuccessMessageHtml(updatedRegistration.messageHtml ?? null);
                setIsSuccess(true);
            } else if (updatedRegistration.errors.form?.length) {
                // The form itself was rejected (e.g. past its closing date)
                setError(updatedRegistration.errors.form[0]);
            }
        } catch (e) {
            setError(m.pre_registration_submit_failed());
            console.error(e);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <div>{m.loading()}</div>;
    }

    if (error || !shop || !registration) {
        return <div>{error || m.pre_registration_load_failed()}</div>;
    }

    // Convert array form to OrderedList format
    const formAsOrderedList: Record<number, FormComponent> = {};
    shop.form.forEach((component, index) => {
        formAsOrderedList[index] = component;
    });

    const handleAnswersChange = (newAnswers: Record<string, string | string[] | object[]>) => {
        // Pass through untouched: the API validates each part's own shape (a select
        // answer is a plain string, orderDetails an object) — wrapping strings in
        // arrays made every select question fail validation server-side.
        setAnswers(newAnswers);
    };

    const content = (
            <PreRegistrationEventProvider event={shop.event}>
                <PageBackground shop={shop}>
                    <Header shopStyle={shop.style} imageFit="contain" />
                    <main className="px-4 py-8">
                        <div className="bg-[var(--shop-panelBackgroundColor)] rounded-lg shadow-lg p-6">
                            {isSuccess ? (
                                <div className="text-center py-8">
                                    <h2 className="text-2xl font-bold mb-4 text-[var(--shop-textColor)]">
                                        {m.pre_registration_success_title()}
                                    </h2>
                                    {successMessageHtml ? (
                                        <div
                                            className="text-[var(--shop-textColor)] text-left [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-1 [&_a]:text-[var(--shop-ctaButtonColor)] [&_a]:underline [&_strong]:font-semibold [&_em]:italic [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-1 [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mb-1 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--shop-borderColor)] [&_blockquote]:pl-3 [&_hr]:my-3 [&_hr]:border-[var(--shop-borderColor)]"
                                            // Authored by the organizer in the backoffice rich-text editor;
                                            // the API sanitizes it through a tag and attribute allowlist
                                            // before it gets here (see RichText on the api side).
                                            dangerouslySetInnerHTML={{ __html: successMessageHtml }}
                                        />
                                    ) : (
                                        <p className="text-[var(--shop-textColor)] whitespace-pre-line">
                                            {successMessage || m.pre_registration_success_default()}
                                        </p>
                                    )}
                                </div>
                            ) : !isPreRegistrationAvailable(shop) ? (
                                <div className="text-center py-8">
                                    <h2 className="text-2xl font-bold mb-4 text-[var(--shop-textColor)]">
                                        {m.pre_registration_closed_title()}
                                    </h2>
                                    <p className="text-[var(--shop-textColor)]">
                                        {m.pre_registration_closed_body()}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-bold mb-6 text-[var(--shop-textColor)]">
                                        {m.pre_registration_title()}
                                    </h2>
                                    {shop.availableUntil && (
                                        <p className="text-[var(--shop-textColor)] mb-6">
                                            {m.pre_registration_available_until({ date: formatClosingDate(shop.availableUntil, activeLanguage) })}
                                        </p>
                                    )}
                                    <CustomerDetailsForm
                                        form={formAsOrderedList}
                                        answers={answers}
                                        errors={registration.errors}
                                        setAnswers={handleAnswersChange}
                                        setFormValid={setIsFormValid}
                                    />
                                    <div className="mt-6">
                                        <MainButton
                                            onClick={handleSubmit}
                                            onDisabledInteraction={revealValidationSummary}
                                            disabled={!isFormValid || isSubmitting}
                                            className="w-full"
                                        >
                                            {isSubmitting ? m.pre_registration_submitting() : m.pre_registration_submit()}
                                        </MainButton>
                                    </div>
                                </>
                            )}
                        </div>
                    </main>
                </PageBackground>
            </PreRegistrationEventProvider>
    );

    // Prefer the event's theme when the API provides one; fall back to legacy shop styling.
    if (shop.theme) {
        return (
            <ThemeStyleOverwrites theme={shop.theme.light}>
                {content}
            </ThemeStyleOverwrites>
        );
    }

    return (
        <StyleOverwrites shopStyle={shop.style}>
            {content}
        </StyleOverwrites>
    );
} 